import type { ChatMessage, TaskContext } from "@project/shared";

/**
 * Turn instructions for a chat card — bare and conversational, with no branch,
 * no gates and no Pre-PR Protocol.
 *
 * A chat card boots Unidentified and stays a conversation until the agent
 * saves a plan. That first plan is the single escalation door: it identifies
 * the card and moves it to In Progress (maybeIdentifyOnFirstPlan, api) and it
 * unlocks the PR path (tool-access.ts gates create_pull_request and `git push`
 * on the same plan). So a plan-less card carries exactly one pointer to that
 * door, and a card that already has a plan gets build guidance instead.
 */
export function buildChatInstructions(
  context: TaskContext,
  scenario: "fresh" | "idle_relaunch" | "feedback_relaunch",
  newMessages: ChatMessage[],
): string[] {
  const userMessages = newMessages.filter((m) => m.role === "user");
  const parts: string[] = [];

  if (scenario === "fresh") {
    parts.push(
      `Read the chat history above and respond to what the team asked.`,
      `Reply with post_to_chat — your turn output is NOT shown in chat, so post_to_chat is the only way the team sees your answer.`,
      `Ask clarifying questions when they help. This is a conversation, not an autonomous build.`,
    );
  } else if (userMessages.length > 0) {
    parts.push(
      `You have new messages on this card.`,
      `\nNew messages since your last run:`,
      ...userMessages.map((m) => `[${m.userName ?? "user"}]: ${m.content}`),
      `\nRespond to them with post_to_chat (your turn output is NOT shown in chat).`,
    );
  } else {
    parts.push(
      `You were relaunched but no new messages have arrived since your last run.`,
      `Post a brief status update with post_to_chat if you still owe the team a response, then wait for them.`,
    );
  }

  if (!context.plan?.trim()) {
    parts.push(
      `\nIf this conversation turns into a development task: save a plan with update_task_plan first — that identifies the card and moves it to In Progress — then implement it and open a PR with mcp__conveyor__create_pull_request, like a normal build.`,
    );
    return parts;
  }

  parts.push(
    `\nThis card has a saved plan, so it is a development task now. Implement the plan on the git branch "${context.githubBranch}", verify with \`bun run check\` and \`bun run test:affected\`, then open the PR with mcp__conveyor__create_pull_request.`,
  );
  if (context.githubPRUrl) {
    parts.push(`An existing PR is open at ${context.githubPRUrl}. Do not create a new PR.`);
  }
  return parts;
}
